// api.ts — client HTTP de la console EDI (MÊME ORIGINE : /v1). En dev, vite proxifie /v1 → 127.0.0.1:3000 ;
// en prod, Fastify sert le SPA + l'API sous le même hôte.
// ⚠️ Le décodage de l'enveloppe §16.2.7 n'est PAS réimplémenté ici : il vient de la PRIMITIVE PARTAGÉE
// `@orgazone/api-envelope/client`, pendant exact de `applyEnvelope` côté serveur — les deux côtés du contrat
// sortent du même package, donc ne peuvent plus diverger. (Un décodeur maison qui lisait `/v1/auth/*` à la
// racine a rendu la connexion impossible le 2026-08-01 ; cf. SPEC §16.2.7bis.) Ne reste ici que ce qui est
// PROPRE à l'EDI : le préfixe, la politique 401, et les formes de données.
import { createApiClient } from '@orgazone/api-envelope/client';
import { bounceOnUnauthorized } from '@orgazone/ui-web';
import type { AuthMode } from '@orgazone/auth-web/client';
import { EMPTY, dateShort, time, timeSec } from '@orgazone/date-format';
import type { BadgeKind } from './components/ui';

// Un SEUL renvoi au login par chargement de page : plusieurs écrans peuvent recevoir 401 en parallèle, et
// chacun déclencherait son propre rechargement dur → tempête de rechargements. Le drapeau les fusionne.

const client = createApiClient({
  baseUrl: '/v1',
  // 401 = session perdue/expirée → retour au login. L'exemption des routes /auth/* n'est plus ici : elle
  // est portée par le client partagé (`isAuthRoute`), qui l'applique à TOUTES les apps.
  // Politique 401 COMMUNE aux trois surfaces (@orgazone/ui-web) : repasse par la GARDE DE ROUTE (qui
  // choisit SSO ou écran local) en gardant la destination. ⚠️ Ne jamais viser `/login` directement :
  // c'est l'écran de bris de glace, et y aller court-circuite la bascule SSO.
  onUnauthorized: bounceOnUnauthorized,
});

// ⚠️ `signal` est transmis tel quel à `fetch` par la primitive partagée : les LECTURES d'écran passent
// celui de leur `useResource`, ce qui ANNULE réellement la requête quand l'écran est quitté (et pas
// seulement son résultat). Les mutations n'en prennent pas : une écriture partie doit aboutir.
//
/*
 * ⚠️ 🔴 LA MESURE DE SANTÉ DU SERVICE A ÉTÉ RETIRÉE D'ICI AVEC SON SEUL LECTEUR (décision
 * utilisateur : la barre du haut n'affiche plus de pastille, la maquette n'en a pas). Ce client notait
 * chaque échange dans `service-health.ts` ; sans l'indicateur, il écrivait dans un état que personne ne
 * consultait — et le `try/catch` qui l'entourait ne faisait plus que relancer l'exception.
 *
 * ⚠️ CE QUE ÇA COÛTE EST ÉCRIT, pas tu : il n'existe plus de témoin PERMANENT que le service répond. Une
 * panne se voit à l'ouverture d'un écran, par l'état d'échec de son `DataGate`, qui en donne le message
 * réel. Le mécanisme retiré vit dans l'historique git si le signal revient ; le rebâtir devra éviter le
 * défaut qu'il corrigeait — déduire la santé du SERVICE de l'état de chargement d'un ÉCRAN, ce qui
 * teignait la barre en rouge pour la requête d'un écran déjà quitté.
 */
const req = <T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
  client.request<T>(method, path, body, signal ? { signal } : undefined);

// Identité de session (routes du socle @orgazone/auth-web — enveloppées comme le reste, cf. `req`).
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
}
// ⚠️ Le type du mode d'auth n'est PAS redéclaré ici : c'est le contrat de plateforme, il vient du package
// qui sert aussi la route (@orgazone/auth-web). Le redéclarer autoriserait une dérive silencieuse.
export type { AuthMode };

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => req<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => req<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => req<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => req<T>('PUT', path, body),
  del: <T>(path: string) => req<T>('DELETE', path),
  // ── Journal : rejeu (§10/§12) ──
  replayMessage: (id: string) => req<{ ok: boolean; status: string; detail: string }>('POST', `/admin/messages/${id}/replay`),
  // ── Transports (catalogue §11) ──
  transportDetail: (id: string) => req<ApiTransportDetail>('GET', `/admin/transports/${id}`),
  setTransportActive: (id: string, active: boolean) => req<{ id: string }>('PATCH', `/admin/transports/${id}`, { active }),
  testTransport: (id: string) => req<ApiTransportTest>('POST', `/admin/transports/${id}/test`),
  // Identité du serveur : confirmer l'empreinte PRÉSENTÉE (celle que l'opérateur a lue), ou oublier la confirmée.
  confirmServerFingerprint: (id: string, fingerprint: string) => req<{ id: string }>('POST', `/admin/transports/${id}/server-fingerprint`, { fingerprint }),
  forgetServerFingerprint: (id: string) => req<{ id: string }>('DELETE', `/admin/transports/${id}/server-fingerprint`),
  // Déclarer l'empreinte que le fournisseur a communiquée : le serveur la ramène à son format et dit si elle correspond
  // à celle que le serveur a présentée (`null` : il n'a encore rien présenté).
  declareServerFingerprint: (id: string, fingerprint: string) =>
    req<{ id: string; fingerprint: string; matchesOffered: boolean | null }>('PUT', `/admin/transports/${id}/server-fingerprint`, { fingerprint }),
  // Relève immédiate (§9) : exécute le vrai tunnel de réception, renvoie ses compteurs réels.
  fetchNow: (id: string) => req<FetchNowResult>('POST', `/admin/transports/${id}/fetch`),
  // ── Utilisateurs (§24.9quinquies : bris de glace + accès fédérés lecture) ──
  users: (signal?: AbortSignal) => req<ApiUser[]>('GET', '/admin/users', undefined, signal),
  audit: (signal?: AbortSignal) => req<ApiAuditEvent[]>('GET', '/admin/audit', undefined, signal),
  rotatePassword: (id: string, password: string) => req<{ id: string }>('POST', `/admin/users/${id}/password`, { password }),
  // ── Maintenance (§5.13) ──
  maintenanceStatus: (signal?: AbortSignal) => req<ApiMaintenanceStatus>('GET', '/admin/maintenance/status', undefined, signal),
  // Restauration : upload du ZIP en corps BINAIRE + confirmation explicite (?confirm=RESTORE). Passe par la
  // primitive comme tout le reste (corps brut via `init`) — pas de décodeur d'enveloppe en double.
  restoreBackup: (file: File): Promise<RestoreReport> =>
    client.request<RestoreReport>('POST', '/admin/maintenance/restore?confirm=RESTORE', undefined, {
      body: file, headers: { 'content-type': 'application/zip' },
    }),
  // ── Auth (socle @orgazone/auth-web) ──
  authMode: () => req<AuthMode>('GET', '/auth/mode'),
  me: () => req<{ user: SessionUser }>('GET', '/auth/me'),
  login: (email: string, password: string, remember: boolean) =>
    req<{ user?: SessionUser; status?: string }>('POST', '/auth/login', { email, password, remember }),
  logout: () => req<{ ok: true }>('POST', '/auth/logout'),
  // Réinitialisation du mot de passe du compte de SECOURS local. ⚠️ `forgot` répond toujours 200 (il ne
  // révèle pas si l'adresse existe) : l'écran ne peut donc PAS affirmer qu'un e-mail est parti.
  forgotPassword: (email: string) => req<{ ok: true }>('POST', '/auth/password/forgot', { email }),
  resetPassword: (token: string, password: string) => req<{ ok: true }>('POST', '/auth/password/reset', { token, password }),
};

// ── Formes renvoyées par l'API (miroir des projections serveur src/db/admin-queries.ts) ──
export interface ApiMessage {
  id: string;
  createdAt: string;
  direction: 'in' | 'out';
  canonicalType: string | null;
  gln: string | null;
  status: string;
  replayCount: number;
  errorDetail: string | null;
  supplierId: string | null;
  supplierName: string | null;
  transportType: string | null;
  siteName: string | null;
  /** La trace ci-dessous a-t-elle été notée ? Faux pour un message reçu avant le suivi des mappings. */
  traceRecorded: boolean;
  /** Le mapping et la version qui l'ont traduit — null s'il ne l'a pas été (ou reçu avant que ce soit noté). */
  mappingId: string | null;
  mappingName: string | null;
  mappingVersion: number | null;
  /** Le flux lu à la réception — null quand rien n'a pu être lu (EDIFACT sans UNH, XML sans racine, fichier vide). */
  flowFormat: string | null;
  flowDiscriminant: string | null;
}

interface ApiEvent {
  id: string;
  messageId: string | null;
  kind: string;
  level: 'info' | 'warn' | 'error';
  detail: unknown;
  createdAt: string;
}

export interface ApiMessageDetail {
  // ⚠️ `replayable` est un VERDICT DU SERVEUR, pas un statut à réinterpréter : la liste des statuts
  // rejouables vit dans `admin/replay.ts` et la console ne la recopie pas (sinon deux copies divergent en
  // silence, et l'écran propose un bouton que le rejeu refuse).
  /** `boReceipt` : le compte rendu du back-office à la confirmation — ce qu'il a écrit et ce qu'il a ÉCARTÉ. */
  message: ApiMessage & { canonical: unknown; rawRef: string | null; fileName: string | null; retentionUntil: string | null; updatedAt: string; replayable: boolean; boReceipt: Record<string, unknown> | null };
  events: ApiEvent[];
}

export interface ApiSupplier {
  id: string;
  name: string;
  gln: string | null; // GLN émetteur du fournisseur (identité EDI ; optionnel)
  active: boolean;
  messageTypes: string[]; // types de messages gérés par ce fournisseur (§6/§11)
  /** Comment il échange, choisi à sa création ; `null` pour un fournisseur ancien qui n'a encore aucun compte. */
  protocol: SupplierProtocol | null;
  /** E-mail : l'adresse où partent nos envois et celles d'où il nous écrit, communes à tous ses clients. */
  emailAddress: string | null;
  emailSenders: string[];
  createdAt: string;
}
/** Les protocoles d'un fournisseur ; `as2` existe au contrat mais n'est pas encore branché. */
export type SupplierProtocol = 'sftp' | 'ftps' | 'ftp' | 'email' | 'as2';
// Ligne ENRICHIE du tableau Fournisseurs (§11) : identité + compteurs + dernier échange + transport principal.
export interface ApiSupplierRow {
  id: string;
  name: string;
  gln: string | null;
  active: boolean;
  messageTypes: string[];
  protocol: SupplierProtocol | null;
  emailAddress: string | null;
  emailSenders: string[];
  createdAt: string;
  mappingsCount: number;
  sitesCount: number;
  errorsCount: number;
  lastExchangeAt: string | null;
  transport: { type: string; host: string | null; configIssue: ConfigIssue | null } | null;
  /** Comptes de transport : combien il en porte, et combien sont ACTIFS.
   *  ⚠️ LES DEUX, parce que « aucun compte » et « tous en pause » sont deux causes distinctes de
   *  non-relève, qui se réparent par deux gestes différents. Un seul nombre ferait deviner l'écran. */
  transportCount: number;
  activeTransportCount: number;
}

/**
 * Types de messages EDI (partagé Fournisseurs « types gérés » + Clients « liens » + Mappings).
 * ⚠️ LE SENS EST UNE PROPRIÉTÉ DU TYPE : un entrant se traduit par un mapping, un sortant est construit par la
 *    plateforme (`server/src/dispatch/build.ts`) et n'en a jamais. Compter un sortant parmi les mappings manquants
 *    annoncerait un manque que personne ne peut combler.
 */
export const MSG_TYPES = [
  { key: 'CATALOG', label: 'Catalogue (PRICAT)', direction: 'in' },
  { key: 'DESPATCH_ADVICE', label: "Avis d'expédition (DESADV)", direction: 'in' },
  { key: 'ORDER_RESPONSE', label: 'Accusé commande (ORDRSP)', direction: 'in' },
  { key: 'INVOICE', label: 'Facture (INVOIC)', direction: 'in' },
  { key: 'REMITTANCE_ADVICE', label: 'Avis de règlement (REMADV)', direction: 'in' },
  { key: 'SALES_REPORT', label: 'Rapport ventes (SLSRPT)', direction: 'out' },
  { key: 'INVENTORY_REPORT', label: 'État de stock (INVRPT)', direction: 'out' },
] as const;
export function msgTypeLabel(k: string): string {
  return MSG_TYPES.find((t) => t.key === k)?.label ?? k;
}
/** Un type entrant — le seul qui se traduit par un mapping. Un type inconnu n'en est pas un. */
export function isInboundType(k: string): boolean {
  return MSG_TYPES.some((t) => t.key === k && t.direction === 'in');
}
/** Les types entrants, dans l'ordre du référentiel. */
export const INBOUND_MSG_TYPES = MSG_TYPES.filter((t) => t.direction === 'in');
interface ApiTransport {
  id: string;
  type: string;
  gln: string | null; // email per-client (§5.2 A) : le client servi par ce compte ; null pour FTP/SFTP (partagé)
  active: boolean;
  createdAt: string;
}
// Comptes de transport — vue catalogue (cross-fournisseur) + détail (config AFFICHABLE sans credentials §13).
export interface ApiTransportListItem {
  id: string;
  supplierId: string;
  supplierName: string | null;
  type: string;
  gln: string | null; // email per-client : client servi ; null pour FTP/SFTP
  active: boolean;
  createdAt: string;
}
// Verdict d'une relève immédiate : compteurs RÉELS du tunnel, ou l'erreur réelle (§23.0).
export interface FetchNowResult {
  ok: boolean;
  error?: string;
  /** Test de connexion refusé sur l'identité du serveur : l'empreinte présentée, à confirmer. */
  serverIdentity?: { offered: string; trusted: string | null };
  fetched?: number;
  translated?: number;
  unrecognized?: number;
  errors?: number;
  misrouted?: number; // traduit mais magasin non vérifié (inconnu, non rattaché, autre que le dossier) → non livré (§5.1)
  duplicate?: number; // document déjà intégré (même n° métier) re-relevé → non réintégré (§7bis)
  // Fichier PRÉSENT mais impossible à relever (réseau, pièce jointe introuvable) : rien n'est acquitté,
  // il repassera à la prochaine relève. Distinct de `errors`, qui compte des fichiers bien reçus.
  unfetched?: number;
  /**
   * Dossiers que le canal n'a pas pu lister, NOMMÉS par le client qu'ils servent.
   *
   * ⚠️ 🔴 UNE RELÈVE PEUT RÉUSSIR *ET* AVOIR SAUTÉ UN CLIENT, et c'est précisément ce que l'écran doit
   * dire : un dossier manquant n'interrompt plus la collecte des autres (il le faisait), donc `ok: true`
   * ne signifie plus « tout a été relevé ». Taire cette liste rendrait le compte-rendu rassurant et faux.
   */
  unreachable?: Array<{ dir: string; label: string; gln: string | null; message: string }>;
  /** Mails refusés sans être relevés (expéditeur non autorisé), marqués lus : leur objet et le motif. */
  refused?: Array<{ dir: string; label: string; name: string; message: string }>;
}
export interface SafeTransportConfig {
  protocol: string;
  host: string | null; // FTP/SFTP : hôte ; email : point IMAP (relève)
  port: number | null;
  user: string | null;
  layout: { mode: string; inboundDir?: string; outboundDir?: string; rootDir?: string; archiveDir?: string };
  smtp?: { host: string; port: number | null; from: string; user: string | null; secure: boolean }; // email : coordonnées d'ENVOI (sans secret §13)
  secure?: boolean; // email : réception IMAP chiffrée — reprise telle quelle à l'édition
  tls?: 'explicit' | 'implicit'; // FTPS : le mode TLS du compte
  allowedSenders?: string[]; // e-mail : les expéditeurs autorisés (vide = personne)
}
/**
 * L'identité du serveur d'un compte SFTP (clé d'hôte SSH) ou FTPS (certificat TLS) : l'empreinte CONFIRMÉE, seule
 * acceptée, et la dernière PRÉSENTÉE puis refusée, que l'écran propose de confirmer.
 */
export interface ApiServerIdentity {
  kind: 'ssh' | 'tls';
  trusted: string | null;
  offered: string | null;
  offeredAt: string | null;
}
/** Pourquoi la configuration d'un compte ne s'affiche pas — le serveur la NOMME, l'écran la dit telle quelle. */
export interface ConfigIssue { kind: 'no_key' | 'no_secret' | 'unreadable'; detail: string }
export interface ApiTransportDetail {
  transport: ApiTransportListItem;
  links: Array<{ gln: string; folderName: string | null; inDir: string | null; outDir: string | null }>;
  config: SafeTransportConfig | null;
  configIssue: ConfigIssue | null;
  serverIdentity: ApiServerIdentity | null; // null : protocole sans identité de serveur (FTP clair, e-mail)
}
// Comptes de la console : `local` = bris de glace (mot de passe local) ; sinon accès fédéré IdP (lecture).
export interface ApiUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  lastLoginAt: string | null;
  createdAt: string;
  local: boolean;
}
export interface ApiAuditEvent {
  id: number;
  action: string;
  email: string | null;
  ip: string | null;
  meta: unknown;
  at: string;
}
// Maintenance (§5.13) : santé des 2 magasins + compteurs (dérivés du réel).
export interface ApiMaintenanceStatus {
  db: { ok: boolean; error?: string };
  storage: { ok: boolean; error?: string };
  counts: { messages: number; suppliers: number; sites: number; mappings: number; transports: number };
  // Les bruts RÉFÉRENCÉS confrontés au contenu RÉEL du stockage. `null` = rien n'a été confronté (un magasin
  // injoignable, ou `rawError` dit pourquoi le contrôle a échoué) — jamais à lire « aucun manquant ».
  raw: RawIntegrity | null;
  rawError?: string;
}
export interface RawIntegrity {
  referenced: number;
  present: number;
  missing: number;
  missingSample: Array<{ messageId: string; rawRef: string }>;
}
// Rapport RÉEL d'une restauration (§23.0 : jamais un succès optimiste).
export interface RestoreReport {
  ok: boolean;
  tables: Record<string, number>;
  skippedTables: string[]; // tables absentes du ZIP (sauvegarde antérieure) — laissées intactes
  files: number;
  fileErrors: string[];
  // Bruts DÉJÀ absents à la sauvegarde : leurs messages reviennent, sans rejeu ni téléchargement possibles.
  missingInArchive: string[];
  // Tables PRÉSENTES dans l'archive que cette version ne restaure pas : leurs lignes ne sont PAS revenues.
  ignoredTables: string[];
  // Mappings d'une sauvegarde antérieure aux mappings partagés, convertis (null = archive déjà au modèle partagé).
  legacyMappingsConverted: number | null;
  // Les identifiants de transport restaurés (chiffrés) sont-ils déchiffrables ICI ? `different-key`/`unknown`
  // = ils sont inertes ou non vérifiables → l'écran DOIT le dire, sinon les relèves meurent en silence (§23.0).
  secrets: { keyMatch: 'same-key' | 'different-key' | 'unknown'; detail: string };
  error?: string;
}
export interface ApiSupplierDetail {
  supplier: ApiSupplier;
  transports: Array<ApiTransport & { config: SafeTransportConfig | null; configIssue: ConfigIssue | null }>; // config AFFICHABLE (sans credentials §13), ou la cause de son absence
  // Les mappings RATTACHÉS au fournisseur — rattaché veut dire en service : un mapping détaché n'apparaît pas ici.
  // `errorCount` : les messages de CE fournisseur que ce mapping a mis en erreur (traduction bloquée) — « à revoir ».
  mappings: Array<{ id: string; name: string; sourceFormat: string; discriminant: string; canonicalType: string; currentVersion: number; errorCount: number }>;
  links: Array<{ id: string; gln: string; name: string | null; messageTypes: string[] }>;
  recent: Array<{ id: string; createdAt: string; direction: 'in' | 'out'; canonicalType: string | null; status: string }>;
}

export interface ApiSite {
  gln: string;
  name: string;
  type: string;
  boTenantRef: string | null;
}
export interface ApiLink {
  id: string;
  supplierId: string;
  supplierName: string | null;
  supplierProtocol: SupplierProtocol | null;
  supplierActive: boolean | null;
  messageTypes: string[];
  folderName: string | null;
  inDir: string | null;
  outDir: string | null;
  /** La boîte de ce client pour un fournisseur par e-mail (sans secret §13) ; `null` : rien n'est relevé ni envoyé. */
  mailbox: { accountId: string; active: boolean; config: SafeTransportConfig | null; configIssue: ConfigIssue | null } | null;
}
/** La boîte d'un client telle que l'écran l'envoie. Un mot de passe vide garde celui enregistré (même serveur, même identifiant). */
export interface MailboxInput {
  imap: { host: string; port?: number; secure: boolean; user: string; password?: string };
  smtp: { host: string; port?: number; secure: boolean; user?: string; password?: string; from: string };
  folder?: string;
  archiveFolder?: string;
}
export interface ApiSiteDetail {
  site: ApiSite & { address: unknown };
  links: ApiLink[];
}

/**
 * Un mapping PARTAGÉ : une identité (nom + flux reconnu + type produit), la version en service et les fournisseurs
 * qu'il traduit. Une nouvelle version s'applique à tous ses fournisseurs à la fois.
 * `discriminant` = ce qui fait reconnaître un fichier : type UNH (EDIFACT), élément racine (XML), premier caractère
 * (positionnel). Un fournisseur ne peut recevoir un même flux que par UN mapping.
 */
export interface ApiMapping {
  id: string;
  name: string;
  sourceFormat: string;
  discriminant: string;
  canonicalType: string;
  currentVersion: number;
  /** Depuis quand la version en service traduit ; null pour un mapping qui n'a pas changé de version depuis que c'est noté. */
  currentSince: string | null;
  versionCount: number;
  /** Messages en erreur parce que sa traduction a bloqué — c'est ce qui le met « à revoir ». */
  errorCount: number;
  suppliers: Array<{ id: string; name: string }>;
  updatedAt: string;
}

// Paramètres : politique de rétention par type (§10) + planification de la relève (§9).
/** Réglage de messagerie sortante (SA-10.5). ⚠️ Le SECRET n'est jamais renvoyé : seulement s'il est défini.
 *  `effective` dit ce qui s'applique VRAIMENT (base / env / aucun), pas ce qui est enregistré. */
export interface ApiMailSettings {
  transport: 'smtp' | 'mailjet' | 'none';
  fromEmail: string | null; fromName: string | null;
  smtpHost: string | null; smtpPort: number | null; smtpSecure: boolean; smtpUser: string | null;
  apiKey: string | null; secretSetAt: string | null;
  updatedAt: string | null; updatedBy: string | null;
  effective: 'base' | 'env' | 'aucun'; note?: string;
}
export interface ApiRetention { id: string; messageType: string; durationDays: number }
export interface ApiSchedule { id: string; scope: string; supplierId: string | null; mode: string; expression: string; active: boolean }

/**
 * CE QUI EST RÉELLEMENT RELEVÉ — `GET /admin/schedules/effectifs`.
 *
 * ⚠️ 🔴 CETTE LISTE NE CONTIENT QUE LES FOURNISSEURS RELEVÉS : actifs, pourvus d'au moins un compte de
 * transport actif, ET d'un rythme qui déclenche. Un fournisseur ABSENT n'est pas « sans rythme », il n'est
 * **pas relevé du tout** — et c'est à l'écran de le croiser avec la liste des fournisseurs pour l'expliquer
 * (`fetch-state.ts`), jamais de l'afficher comme s'il l'était (§23.0).
 * ⚠️ IL N'Y A PLUS D'ORIGINE À RENDRE depuis le retrait du rythme commun : un fournisseur a SON rythme, ou
 * il n'est pas relevé. Un cron, ou rien.
 */
export interface ApiEffectiveRhythm {
  supplierId: string;
  supplierName: string;
  cron: string;
  /**
   * Prochain déclenchement, en ISO.
   * ⚠️ 🔴 `null` = L'HEURE EST INCONNUE, jamais « bientôt » ni une date devinée. Calculé côté serveur avec
   * l'ÉVALUATEUR DE pg-boss (même bibliothèque, même version, même fuseau) : l'heure annoncée est donc celle
   * qui déclenchera, à la minute près — l'écran ne doit pas afficher de secondes.
   */
  nextAt: string | null;
}

/**
 * LE DÉPÔT SORTANT NOCTURNE — une planification comme les autres, donc dans la même liste.
 * ⚠️ IL VIT AVEC LES RELÈVES ET NON DANS UN AUTRE ONGLET : « quand va-t-on chercher » et « quand va-t-on
 * déposer » sont la même question pour l'exploitant. Son HEURE se règle en revanche dans Général
 * (`dispatchTime`), et l'écran le dit — l'inventer ici créerait une seconde voie d'écriture.
 */
export interface ApiDispatchSchedule {
  cron: string | null;
  /** L'heure telle que l'exploitant l'a tapée (`HH:MM`), ou `null` si elle vient de l'environnement. */
  at: string | null;
  nextAt: string | null;
  /** Sortants prêts à déposer — la file réelle, pas une estimation. */
  pending: number;
}

/** Tout ce qui est planifié, en UNE lecture : `GET /admin/schedules/effectifs`. */
export interface ApiSchedulesView {
  fetch: ApiEffectiveRhythm[];
  dispatch: ApiDispatchSchedule;
  /** Le fuseau dans lequel toutes ces heures sont exprimées — l'écran doit le NOMMER, pas le supposer. */
  timezone: string;
}
// Détail d'un mapping : son historique de versions et les règles EN SERVICE (le « Rapport »).
export interface ApiMappingDetail {
  id: string;
  name: string;
  sourceFormat: string;
  discriminant: string;
  canonicalType: string;
  currentVersion: number;
  currentSince: string | null;
  suppliers: Array<{ id: string; name: string }>;
  versions: Array<{ version: number; createdAt: string }>;
  rules: unknown;
  /** Les champs canoniques que la version en service alimente (en-tête, et ceux de chaque ligne sous `lines.path`). */
  producedFields: { header: string[]; lines: { path: string; fields: string[] } | null } | null;
  errorCount: number;
  /** Le dernier rapport CONSERVÉ de la version en service — null si elle a été mise en service sans fichier exemple. */
  report: StoredMappingReport | null;
  /** Le fichier que « Revérifier » relirait ; null = rien de ce flux n'a été reçu de ses fournisseurs. */
  recheckFile: { messageId: string; fileName: string | null; receivedAt: string } | null;
  createdAt: string;
  updatedAt: string;
}
interface MappingDiagnostic {
  kind: 'err' | 'warn';
  title: string;
  detail: string;
}
/** Le rapport du moteur sur un fichier — même forme pour la validation, l'import et la revérification. */
export interface MappingReport {
  recognized: boolean;
  reason?: string;
  flow: { format: string; discriminant: string } | null;
  canonicalType?: string;
  gln?: string | null;
  hasErrors?: boolean;
  valuesTranslated?: number;
  blocking: number;
  warnings: number;
  diagnostics: MappingDiagnostic[];
  diagnosticsTotal: number;
  /** Un APERÇU : les listes longues sont coupées, et nommées dans `cut` avec leur longueur réelle. */
  canonical?: unknown;
  cut: Array<{ path: string; total: number; kept: number }>;
}
export interface StoredMappingReport {
  checkedAt: string;
  /** `sample` : le fichier exemple déposé à l'import ; `recheck` : le dernier fichier reçu, revérifié. */
  source: 'sample' | 'recheck';
  fileName: string | null;
  messageId: string | null;
  report: MappingReport;
}
export interface MappingValidateResult {
  valid: boolean;
  shapeErrors?: string[];
  /** Redirections de magasin déguisées en table de codes — refus distinct d'une erreur de FORME (§5.1). */
  storeRedirections?: string[];
  format?: string;
  messageType?: string;
  canonicalType?: string;
  report?: MappingReport | null;
}

/**
 * Verdict du test de connexion, DOSSIER PAR DOSSIER.
 *
 * ⚠️ 🔴 POURQUOI UNE LISTE ET PAS UN BOOLÉEN : le test sondait la racine du compte et rendait `ok`, pendant
 * que la relève échouait sur `550 Directory not found.` faute du dossier d'UN client. Il sonde désormais
 * chaque dossier que la relève visite, et l'écran doit pouvoir NOMMER ceux qui manquent — un verdict global
 * ne permet aucun geste.
 */
export interface ApiTransportTest {
  ok: boolean;
  /** Résumé en tête, déjà composé côté serveur : « client — message réel du serveur » par dossier fautif. */
  error?: string;
  dossiers: Array<{ dossier: string; client: string; gln: string | null; ok: boolean; error?: string }>;
  /** Présent quand le serveur a été refusé sur son identité : l'empreinte présentée, et celle confirmée s'il y en a une. */
  serverIdentity?: { offered: string; trusted: string | null };
}

/** Une ligne du bloc « Connexions » : un compte de transport et ses 12 tranches de 2 h. */
export interface ApiConnectionHeat {
  accountId: string;
  name: string;
  state: 'ok' | 'err' | 'off';
  cells: Array<'ok' | 'err' | 'none'>;
  exchanges: number;
  failures: number;
}

export interface ApiDashboard {
  window: string;
  /** Fuseau dans lequel `volume` et `connections` sont découpés — l'heure que l'opérateur lit. */
  timezone: string;
  /** Heure du dépôt nocturne (réglage), ou `null` si rien n'est réglé — jamais une heure inventée. */
  dispatchTime: string | null;
  /** ⚠️ `misrouted` est projeté pour que l'écran puisse NOMMER le geste attendu, pas seulement le compter. */
  totals: { exchanges: number; inbound: number; outbound: number; errors: number; unrecognized: number; misrouted: number; confirmed: number; pendingOutbound: number; deadLetter: number };
  successRate: number | null;
  byStatus: Record<string, number>;
  /**
   * ⚠️ 🔴 CINQ SEAUX, ET LEUR SOMME ÉGALE `totals.exchanges` — c'est ce qui rend l'anneau lisible. Les quatre
   * d'avant laissaient dehors `unrecognized`, `misrouted`, `duplicate` et `received` : mesuré sur la bêta,
   * l'anneau sortait **100 % turquoise** là où son centre annonçait **75,1 %**, un message sur quatre
   * demandant une action. Un anneau dont les parts ne couvrent pas le tout ne dit rien du tout.
   */
  funnel: { confirmed: number; pushedNotConfirmed: number; inFlight: number; toAction: number; moot: number };
  /** ⚠️ TOUJOURS 12 tranches, vides comprises : un trou doit se voir comme un trou (§23.0). */
  volume: Array<{ label: string; inbound: number; outbound: number }>;
  connections: ApiConnectionHeat[];
  queue: Array<{ supplierName: string; types: string; n: number }>;
  recent: ApiMessage[];
}

// ── Statut interne → pastille + libellé FR (partagé journal + supervision) ──
const STATUS_KIND: Record<string, BadgeKind> = {
  confirmed: 'ok',
  translated: 'traduit',
  pushed: 'pousse',
  received: 'recu',
  unrecognized: 'warn',
  error: 'err',
  misrouted: 'warn', // ambre : routage à corriger (dossier ou correspondance), distinct du rouge « erreur » (push)
  duplicate: 'off', // neutre : bénin, AUCUNE action attendue (le document est déjà intégré une fois §7bis)
};
const STATUS_LABEL: Record<string, string> = {
  confirmed: 'confirmé',
  translated: 'traduit',
  // ⚠️ « déposé » et non « poussé » (relevé de la maquette) : cette pastille sert aux DEUX SENS, et un
  // sortant n'est pas « poussé » — il est déposé chez le fournisseur. Les étapes du volet, elles, restent
  // propres à leur sens (« Poussé au BO » / « Déposé »), parce qu'elles n'ont qu'un destinataire à nommer.
  pushed: 'déposé',
  received: 'reçu',
  unrecognized: 'non reconnu',
  misrouted: 'mauvais magasin',
  duplicate: 'déjà intégré',
  error: 'erreur',
};

/** URL de téléchargement du brut d'un message (§7) — utilisée en lien direct (le cookie de session part avec). */
export function rawDownloadUrl(id: string): string {
  return `/v1/admin/messages/${id}/raw`;
}

/** URL de téléchargement de la sauvegarde ZIP (§5.13) — lien direct (cookie de session envoyé). */
export function backupDownloadUrl(): string {
  return '/v1/admin/maintenance/backup';
}

/** Statut interne → pastille (défaut neutre `off` si inconnu). */
export function statusKind(status: string): BadgeKind {
  return STATUS_KIND[status] ?? 'off';
}
/** Statut interne → libellé FR (défaut = le statut brut). */
export function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}
/**
 * Statut d'un COMPTE → libellé FR.
 *
 * ⚠️ 🔴 L'ÉCRAN RENDAIT LA CLÉ BRUTE (« active », « disabled ») dans une console par ailleurs française :
 * un statut est une donnée du socle d'auth, pas un mot d'interface, et il doit être NOMMÉ avant d'être
 * affiché. La liste est FERMÉE sur l'énumération du serveur (`active | invited | disabled`) ; un statut
 * inconnu retombe sur sa clé — visible, donc corrigeable, plutôt qu'un blanc.
 */
const USER_STATUS_LABEL: Record<string, string> = {
  active: 'actif',
  invited: 'invité',
  disabled: 'suspendu', // le mot de la maquette ; « disabled » ne dit rien à un exploitant
};
export function userStatusLabel(status: string): string {
  return USER_STATUS_LABEL[status] ?? status;
}

/**
 * Tous les statuts que la console sait nommer.
 *
 * ⚠️ DÉRIVÉE DE LA TABLE, jamais recopiée : c'est la liste que le banc de progression confronte à la sienne,
 * pour qu'un statut ajouté ici sans être appris là-bas fasse TOMBER la porte — sinon le volet le rendrait à
 * l'étape 0 et cesserait de se relire, sans erreur (§23.0).
 */
export function knownStatuses(): string[] {
  return Object.keys(STATUS_LABEL);
}
/** Heure locale HH:MM:SS d'un ISO (affichage horodatage). Rendu partagé (@orgazone/date-format). */
export const hhmmss = (iso: string): string => timeSec(iso);
/** Horodatage COURT relatif (« dernier échange ») : l'heure si c'est aujourd'hui, sinon JJ/MM ; « — » si absent. */
export function shortStamp(iso: string | null): string {
  if (!iso) return EMPTY;
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return sameDay ? time(d) : dateShort(d);
}
